// /admin/analisis-catalogo
// Módulo para analizar catálogos PDF de proveedores.
//
// Flow eficiente:
// 1. Nico sube PDF (puede ser grande — Boyerco 397pgs)
// 2. Frontend usa pdf-lib para dividir en chunks de 15 pgs
// 3. Envía chunks secuenciales a Claude Vision → extrae productos
// 4. Todo se guarda en tabla `analisis_catalogo_items` (funciona como "sheet")
// 5. Nico ve el sheet, filtra, edita, y ELIGE cuáles validar en ML (on-demand)
// 6. Solo los seleccionados consultan ML API → economiza tokens y foco

import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PDFDocument } from 'pdf-lib'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const CHUNK_SIZE = 15  // páginas por request a Claude (seguro para no timeout)

// Convierte Uint8Array grande a base64 sin explotar el stack.
// El método naive `btoa(String.fromCharCode(...bytes))` truena con >65k bytes.
async function bytesToBase64(bytes) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      resolve(dataUrl.split(',')[1] || '')
    }
    reader.onerror = reject
    reader.readAsDataURL(new Blob([bytes]))
  })
}

export default function AnalisisCatalogo() {
  const nav = useNavigate()
  const [analisis, setAnalisis] = useState([])
  const [loading, setLoading] = useState(true)
  const [cargando, setCargando] = useState(false)
  const [progreso, setProgreso] = useState(null)  // {actual, total, mensaje}
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)

  const [proveedor, setProveedor] = useState('Boyerco')
  const [archivo, setArchivo] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase.from('analisis_catalogo').select('*').order('created_at', { ascending: false }).limit(20)
    setAnalisis(data || [])
    setLoading(false)
  }

  async function dividirPdf(archivoFile) {
    const arrayBuffer = await archivoFile.arrayBuffer()
    const pdfDoc = await PDFDocument.load(arrayBuffer)
    const totalPaginas = pdfDoc.getPageCount()

    const chunks = []
    for (let start = 0; start < totalPaginas; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE, totalPaginas)
      const chunkDoc = await PDFDocument.create()
      const paginasIndices = []
      for (let i = start; i < end; i++) paginasIndices.push(i)
      const copiadas = await chunkDoc.copyPages(pdfDoc, paginasIndices)
      copiadas.forEach(p => chunkDoc.addPage(p))
      const bytes = await chunkDoc.save()
      // Convertir a base64 usando FileReader (seguro para arrays grandes)
      const base64 = await bytesToBase64(bytes)
      chunks.push({
        pagina_desde: start + 1,
        pagina_hasta: end,
        base64,
        size_mb: (bytes.length / 1024 / 1024).toFixed(2),
      })
    }
    return { totalPaginas, chunks }
  }

  async function subirYAnalizar() {
    if (!archivo) { setError('Elegí un PDF'); return }
    if (!proveedor.trim()) { setError('Poné el nombre del proveedor'); return }
    setError(null)
    setInfo(null)
    setCargando(true)
    setProgreso({ actual: 0, total: 0, mensaje: 'Preparando PDF...' })

    try {
      // 1) Dividir el PDF en chunks
      setProgreso({ actual: 0, total: 1, mensaje: '📖 Leyendo y dividiendo PDF...' })
      const { totalPaginas, chunks } = await dividirPdf(archivo)
      setProgreso({ actual: 0, total: chunks.length, mensaje: `✅ PDF dividido en ${chunks.length} chunks (${totalPaginas} pgs total)` })

      // 2) Procesar chunk por chunk con Claude
      let analisisId = null
      let totalExtraidos = 0
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i]
        setProgreso({
          actual: i,
          total: chunks.length,
          mensaje: `🤖 Claude procesando chunk ${i + 1}/${chunks.length} (pgs ${chunk.pagina_desde}-${chunk.pagina_hasta}, ${chunk.size_mb}MB)...`,
        })

        const { data: resp, error: e } = await supabase.functions.invoke('analizar-catalogo-pdf', {
          body: {
            pdf_base64: chunk.base64,
            proveedor: proveedor.trim(),
            archivo_nombre: archivo.name,
            paginas_desde: chunk.pagina_desde,
            paginas_hasta: chunk.pagina_hasta,
            analisis_id: analisisId,  // en la primera es null, después se reusa
          },
        })

        if (e) throw new Error(`Chunk ${i + 1}: ${e.message}`)
        if (resp?.error) throw new Error(`Chunk ${i + 1}: ${resp.error}${resp.detail ? ' — ' + resp.detail : ''}`)

        if (!analisisId) analisisId = resp.analisis_id
        totalExtraidos += resp.productos_extraidos || 0

        setProgreso({
          actual: i + 1,
          total: chunks.length,
          mensaje: `✅ Chunk ${i + 1}/${chunks.length} listo · ${resp.productos_extraidos} productos extraídos (total: ${totalExtraidos})`,
        })
      }

      setInfo(`🎉 Extracción completa: ${totalExtraidos} productos de ${totalPaginas} páginas. Ahora podés revisar y validar en ML solo los que te interesen.`)
      setArchivo(null)
      await cargar()

      // Ir directo al detalle
      setTimeout(() => nav(`/admin/analisis-catalogo/${analisisId}`), 1500)
    } catch (e) {
      setError(e.message)
      setProgreso(null)
    } finally {
      setCargando(false)
    }
  }

  const totalGastadoApi = useMemo(() => {
    return analisis.reduce((sum, a) => sum + Number(a.costo_api_estimado || 0), 0)
  }, [analisis])

  return (
    <AdminLayout title="Análisis de catálogos">
      <div style={{ padding: 24, maxWidth: 1100 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, color: '#1a3461', fontSize: 26 }}>🔍 Análisis de catálogos de proveedores</h1>
          <p style={{ color: '#64748b', marginTop: 8, maxWidth: 780 }}>
            Subí un PDF de un proveedor (Boyerco, Prolimpio, etc.). El sistema divide el PDF en chunks,
            extrae los productos con Claude Vision y los muestra en un sheet.
            Después vos elegís cuáles validar en MercadoLibre (para no gastar consultas de más).
          </p>
        </div>

        {/* Upload */}
        <div style={{
          background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0',
          marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 14px', color: '#1a3461', fontSize: 16 }}>📤 Subir catálogo nuevo</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Proveedor</div>
              <input
                type="text"
                value={proveedor}
                onChange={e => setProveedor(e.target.value)}
                placeholder="Ej: Boyerco"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
                disabled={cargando}
              />
            </label>

            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Archivo PDF (cualquier tamaño)</div>
              <input
                type="file"
                accept="application/pdf"
                onChange={e => setArchivo(e.target.files?.[0] || null)}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, background: 'white' }}
                disabled={cargando}
              />
            </label>
          </div>

          {archivo && (
            <div style={{ fontSize: 12, color: '#0369a1', marginBottom: 12 }}>
              📎 {archivo.name} · {(archivo.size / 1024 / 1024).toFixed(2)} MB
              {archivo.size > 5 * 1024 * 1024 && (
                <span style={{ marginLeft: 8, background: '#fef3c7', color: '#78350f', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>
                  Se dividirá automáticamente en chunks de {CHUNK_SIZE} páginas
                </span>
              )}
            </div>
          )}

          <button
            onClick={subirYAnalizar}
            disabled={cargando || !archivo}
            style={{
              padding: '10px 20px',
              background: cargando ? '#94a3b8' : '#00A8B5',
              color: 'white',
              border: 0,
              borderRadius: 8,
              fontWeight: 700,
              cursor: cargando ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            {cargando ? '⏳ Procesando...' : '🚀 Extraer productos del PDF'}
          </button>

          {progreso && (
            <div style={{
              marginTop: 12, padding: 12, background: '#eff6ff', border: '1px solid #93c5fd',
              borderRadius: 8, color: '#1e40af', fontSize: 12,
            }}>
              <div style={{ marginBottom: 6, fontWeight: 700 }}>{progreso.mensaje}</div>
              {progreso.total > 0 && (
                <div style={{ height: 8, background: '#dbeafe', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${(progreso.actual / progreso.total) * 100}%`,
                    background: '#3b82f6',
                    transition: 'width 0.3s',
                  }} />
                </div>
              )}
            </div>
          )}

          {info && !progreso && (
            <div style={{
              marginTop: 12, padding: 10, background: '#d1fae5', border: '1px solid #86efac',
              borderRadius: 6, color: '#065f46', fontSize: 12,
            }}>
              {info}
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 12, padding: 10, background: '#fee2e2', border: '1px solid #fca5a5',
              borderRadius: 6, color: '#991b1b', fontSize: 12,
            }}>
              ⚠ {error}
            </div>
          )}
        </div>

        {/* Historial */}
        <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, color: '#1a3461', fontSize: 16 }}>📚 Análisis previos</h3>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              Total gastado en Claude API: <strong>${totalGastadoApi.toFixed(2)} USD</strong>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>
          ) : analisis.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
              Todavía no analizaste ningún catálogo. Subí uno arriba para empezar.
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: 8, textAlign: 'left', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Fecha</th>
                  <th style={{ padding: 8, textAlign: 'left', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Proveedor</th>
                  <th style={{ padding: 8, textAlign: 'left', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Archivo</th>
                  <th style={{ padding: 8, textAlign: 'right', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Productos</th>
                  <th style={{ padding: 8, textAlign: 'right', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Nuevos</th>
                  <th style={{ padding: 8, textAlign: 'right', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Costo API</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {analisis.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, color: '#64748b' }}>{new Date(a.created_at).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: 8, fontWeight: 600, color: '#1a3461' }}>{a.proveedor}</td>
                    <td style={{ padding: 8, color: '#64748b', fontSize: 11 }}>{a.archivo_nombre || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{a.total_productos || 0}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#16a34a', fontWeight: 700 }}>{a.productos_nuevos || 0}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#64748b', fontSize: 11 }}>${Number(a.costo_api_estimado || 0).toFixed(3)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        onClick={() => nav(`/admin/analisis-catalogo/${a.id}`)}
                        style={{ padding: '4px 10px', background: '#0ea5e9', color: 'white', border: 0, borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Ver sheet
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, fontSize: 11, color: '#64748b' }}>
          💡 <strong>Estrategia eficiente:</strong> el sistema divide el PDF automáticamente en chunks de {CHUNK_SIZE} páginas.
          Solo Claude Vision procesa el PDF (una vez, ~$0.02-0.05 por chunk).
          La validación de precios en ML se hace <strong>on-demand</strong> desde el sheet, para no gastar de más.
        </div>
      </div>
    </AdminLayout>
  )
}
